import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MenuItem } from '@mui/material';
import { Upload } from 'lucide-react';

import { DOCIcon, PDFIcon } from '@labradorsports/assets';
import {
    animatedModal,
    AnimatedModalProps,
    Checkbox,
    Modal,
    StyledSelectField,
    StyledTextField,
    WebsitePreview,
} from '@labradorsports/components';
import { DocumentTypes } from '@labradorsports/constants';
import { isValidUrl } from '@labradorsports/utils';

import { addValidation, useValidation, useDispatch } from '../../shared/index.js';
import { messagingApi, Selectors, useSaveTeamDocumentMutation } from '../../store/index.js';

interface OwnProps {
    editing?: any;
}

type Props = AnimatedModalProps & OwnProps;

const UploadDocumentModal: FC<Props> = ({ onClose, editing }) => {
    const dispatch = useDispatch();
    const [addTeamDocument] = useSaveTeamDocumentMutation();
    const uid = useSelector(Selectors.uid);
    const activeTeam = useSelector(Selectors.activeTeam);
    const fileInputRef = useRef(null);
    const [uploadType, setUploadType] = useState('');
    const [uploadName, setUploadName] = useState('');
    const [webUrl, setWebUrl] = useState('');
    const [requestAcknowledgement, setRequestAcknowledgement] = useState(false);
    const [chosenFile, setChosenFile] = useState(null);
    const [websiteFavicon, setWebsiteFavicon] = useState('');
    const [websiteTitle, setWebsiteTitle] = useState('');
    const [websiteDescription, setWebsiteDescription] = useState('');

    const { errors, submitted, setSubmitted, validateRef } = useValidation(async () => {
        const newErrors: any = {
            type: !uploadType,
        };

        if (uploadType) {
            newErrors.name = !uploadName;

            if (uploadType === DocumentTypes.FILE) {
                newErrors.file = !chosenFile;
            } else {
                newErrors.url = !webUrl;
            }
        }

        return newErrors;
    }, [uploadType, uploadName, webUrl, chosenFile]);

    const reset = () => {
        setUploadName('');
        setWebsiteTitle('');
        setWebsiteDescription('');
        setWebsiteFavicon('');
    };

    useEffect(() => {
        reset();

        if (editing) {
            setUploadName(editing.name);
            setUploadType(editing.type);
            setWebUrl(editing.url);
        }
    }, [editing]);

    const uploadTypeChange = (evt) => {
        setUploadType(evt.target.value);
    };

    const webUrlChange = (evt) => {
        setWebUrl(evt.target.value);
    };

    const uploadNameChange = (evt) => {
        setUploadName(evt.target.value);
    };

    const requestAcknowledgementChange = (evt, checked) => {
        setRequestAcknowledgement(checked);
    };

    const selectFile = (evt: any) => {
        if (evt.target.files.length > 0) {
            const [file] = fileInputRef.current.files;
            if (
                [
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ].includes(file.type)
            ) {
                setChosenFile(file);
            }
        }
    };

    const fileDrop = (evt: any) => {
        evt.preventDefault();

        if (evt.dataTransfer.items) {
            [...evt.dataTransfer.items].forEach((item) => {
                // If dropped items aren't files, reject them
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    setChosenFile(file);
                }
            });
        } else {
            // Use DataTransfer interface to access the file(s)
            [...evt.dataTransfer.files].forEach((file) => {
                setChosenFile(file);
            });
        }
    };

    const webUrlBlur = async () => {
        const validUrl = [webUrl, `https://${webUrl}`].find(isValidUrl);

        if (validUrl) {
            setWebUrl(validUrl);
            const {
                data: { title, description, imagePreview },
            } = await dispatch(
                messagingApi.endpoints.getLinkPreview.initiate({
                    url: validUrl,
                })
            );
            setUploadName(title);
            setWebsiteTitle(title);
            setWebsiteDescription(description);
            setWebsiteFavicon(imagePreview);
        }
    };

    const submit = async () => {
        if (await validateRef.current()) {
            const document: any = {
                type: uploadType,
                name: uploadName,
            };

            if (editing) {
                document.id = editing.id;
            }

            if (uploadType === DocumentTypes.FILE) {
                document.file = chosenFile;
                document.requestAcknowledgement = requestAcknowledgement;
            } else {
                document.url = webUrl;

                if (websiteFavicon) {
                    document.previewImage = websiteFavicon;
                }
            }

            await addTeamDocument({
                uid,
                document,
                activeTeam,
            });

            onClose();
        } else {
            setSubmitted(true);
        }
    };

    const buttons = [
        { text: 'Cancel' },
        {
            text: 'Share',
            variant: 'contained',
            color: 'primary',
            action: submit,
        },
    ];

    return (
        <Modal
            className="upload-document-modal"
            title="Upload Document or Quick Link"
            icon={<Upload />}
            buttons={buttons}
            onClose={onClose}
        >
            <div>
                <StyledSelectField
                    label="Upload Type"
                    value={uploadType}
                    onChange={uploadTypeChange}
                    helperText={submitted && errors.type && 'Please select a type'}
                    error={submitted && errors.type}
                >
                    <MenuItem value={DocumentTypes.FILE}>File</MenuItem>
                    <MenuItem value={DocumentTypes.LINK}>Web Link</MenuItem>
                </StyledSelectField>
            </div>
            {uploadType === DocumentTypes.LINK ? (
                <div>
                    <StyledTextField
                        label="Web URL"
                        value={webUrl}
                        onChange={webUrlChange}
                        onBlur={webUrlBlur}
                        helperText={submitted && errors.url && 'Please enter a url'}
                        error={submitted && errors.url}
                        fullWidth
                    />
                </div>
            ) : null}
            {uploadType === DocumentTypes.FILE || uploadName !== '' ? (
                <div>
                    <StyledTextField
                        label="Document Name"
                        value={uploadName}
                        onChange={uploadNameChange}
                        helperText={submitted && errors.name && 'Please enter a document name'}
                        error={submitted && errors.name}
                        fullWidth
                    />
                </div>
            ) : null}
            {uploadType === DocumentTypes.FILE ? (
                <div>
                    <Checkbox
                        value={requestAcknowledgement}
                        onChange={requestAcknowledgementChange}
                        label="Request player acknowledgement"
                    />
                </div>
            ) : null}
            {uploadType === DocumentTypes.LINK && websiteTitle ? (
                <WebsitePreview
                    url={webUrl}
                    previewImage={websiteFavicon}
                    title={websiteTitle}
                    description={websiteDescription}
                />
            ) : null}
            {uploadType === DocumentTypes.FILE ? (
                <div className="file-upload">
                    {chosenFile ? (
                        <>
                            <div className="csv-file-selected">
                                {chosenFile.type === 'application/pdf' ? (
                                    <PDFIcon className="file-type-icon" />
                                ) : (
                                    <DOCIcon className="file-type-icon" />
                                )}
                                <div>
                                    <b>{chosenFile.name}</b> has been selected.
                                </div>
                            </div>
                            <div className="text-center">
                                Wrong file? <label htmlFor="doc-upload">Browse again</label>
                            </div>
                        </>
                    ) : (
                        <div className="file-drop-zone" onDrop={fileDrop} onDragOver={(e) => e.preventDefault()}>
                            <div>
                                {chosenFile ? (
                                    <>
                                        {chosenFile.type === 'application/pdf' ? (
                                            <PDFIcon className="file-type-icon me-3" />
                                        ) : (
                                            <DOCIcon className="file-type-icon me-3" />
                                        )}
                                    </>
                                ) : null}
                            </div>
                            <div>
                                Drop your document here, or <label htmlFor="doc-upload">browse</label>
                            </div>
                        </div>
                    )}
                    <input
                        type="file"
                        id="doc-upload"
                        accept=".pdf,.doc,.docx"
                        className="file-input"
                        ref={fileInputRef}
                        onChange={selectFile}
                    />
                    {submitted && errors.file ? <div className="small text-danger">Please select a file</div> : null}
                </div>
            ) : null}
        </Modal>
    );
};

export default animatedModal<Props>(addValidation(UploadDocumentModal), { disableBackdropClick: true });
